import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../components/PageContainer';
import { trpc } from '../../lib/trpc';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUSES,
  type FeedbackCategory,
  type FeedbackStatus,
} from '@wineryxplorer/shared';

const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  data_error: 'Data error',
  bug: 'Bug',
  feature_request: 'Feature request',
  contact_request: 'Contact request',
  other: 'Other',
};

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

type StatusFilter = FeedbackStatus | 'pending' | 'all';
type CategoryFilter = FeedbackCategory | 'all';

export const AdminFeedback = () => {
  const navigate = useNavigate();
  // Default to "pending" so the page opens on actionable items.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

  const utils = trpc.useUtils();

  // "pending" is two queries merged (new + in_progress), so the server only
  // supports a single status filter at a time. Use two queries and concat.
  const pendingNew = trpc.feedback.list.useQuery(
    { status: 'new', category: categoryFilter === 'all' ? undefined : categoryFilter, limit: 100 },
    { enabled: statusFilter === 'pending' }
  );
  const pendingInProgress = trpc.feedback.list.useQuery(
    {
      status: 'in_progress',
      category: categoryFilter === 'all' ? undefined : categoryFilter,
      limit: 100,
    },
    { enabled: statusFilter === 'pending' }
  );
  const singleStatus = trpc.feedback.list.useQuery(
    {
      status: statusFilter === 'all' ? undefined : (statusFilter as FeedbackStatus),
      category: categoryFilter === 'all' ? undefined : categoryFilter,
      limit: 100,
    },
    { enabled: statusFilter !== 'pending' }
  );

  const isLoading =
    statusFilter === 'pending'
      ? pendingNew.isLoading || pendingInProgress.isLoading
      : singleStatus.isLoading;

  const rawItems =
    statusFilter === 'pending'
      ? [...(pendingNew.data?.items ?? []), ...(pendingInProgress.data?.items ?? [])].sort((a, b) =>
          a.createdAt < b.createdAt ? 1 : -1
        )
      : (singleStatus.data?.items ?? []);

  // Defensive: server's safeObjectIdToString can in principle return undefined.
  // Skip rows without an _id so the mutation always has a valid string id.
  const items = rawItems.filter((row): row is typeof row & { _id: string } => Boolean(row._id));

  const error = pendingNew.error ?? pendingInProgress.error ?? singleStatus.error;

  const updateStatus = trpc.feedback.updateStatus.useMutation({
    onSuccess: () => {
      utils.feedback.list.invalidate();
      utils.feedback.pendingCount.invalidate();
    },
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Feedback</h1>
        <p className="text-gray-600 mt-2">
          Review and resolve user-submitted feedback, bug reports, feature requests, and contact
          requests.
        </p>
      </div>

      <PageContainer>
        <div className="flex flex-wrap gap-3 mb-4">
          <label className="text-sm">
            Status&nbsp;
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="rounded border border-gray-300 bg-gray-50 px-2 py-1"
            >
              <option value="pending">Pending (new + in progress)</option>
              {FEEDBACK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
              <option value="all">All</option>
            </select>
          </label>
          <label className="text-sm">
            Category&nbsp;
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
              className="rounded border border-gray-300 bg-gray-50 px-2 py-1"
            >
              <option value="all">All</option>
              {FEEDBACK_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {isLoading && <p className="text-gray-500">Loading…</p>}
        {error && <p className="text-error">{error.message}</p>}
        {!isLoading && !error && items.length === 0 && (
          <p className="text-gray-500">No feedback matches these filters.</p>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3">From</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 w-px" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const submitter =
                    typeof row.submittedBy === 'object'
                      ? `${row.submittedBy.userName ?? '—'} (${row.submittedBy.email ?? ''})`
                      : String(row.submittedBy);
                  const goToDetail = () => navigate(`/admin/feedback/${row._id}`);
                  return (
                    <tr
                      key={row._id}
                      onClick={goToDetail}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToDetail();
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open feedback from ${submitter}`}
                      className="group border-b border-gray-100 align-top cursor-pointer transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:bg-gray-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-700"
                    >
                      <td className="py-2 pr-3 whitespace-nowrap text-gray-600">
                        {new Date(row.createdAt).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3">{CATEGORY_LABEL[row.category]}</td>
                      <td className="py-2 pr-3">{submitter}</td>
                      {/* Status cell: stop click + keydown from bubbling so the
                          inline dropdown doesn't navigate to the detail page. */}
                      <td
                        className="py-2 pr-3"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <select
                          value={row.status}
                          disabled={updateStatus.isPending}
                          onChange={(e) =>
                            updateStatus.mutate({
                              id: row._id,
                              status: e.target.value as FeedbackStatus,
                            })
                          }
                          className="rounded border border-gray-300 bg-gray-50 px-2 py-1"
                        >
                          {FEEDBACK_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </option>
                          ))}
                        </select>
                      </td>
                      {/* Chevron affordance — always visible, deepens on hover. */}
                      <td className="py-2 pr-3 text-gray-400 group-hover:text-primary-700 group-focus-visible:text-primary-700 transition-colors" aria-hidden="true">
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 5l6 5-6 5" />
                        </svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </PageContainer>
    </div>
  );
};
